import { Link } from "react-router-dom";
import { Facebook, Globe, Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { buildWhatsAppLink, GENERIC_WA_MESSAGE } from "@/lib/whatsapp";
import { track } from "@/lib/track";
import { COMPANY, NAV, scrollToId } from "@/lib/content";

const SOCIAL = [
  { icon: Facebook, label: "Facebook" },
  { icon: Instagram, label: "Instagram" },
  { icon: Linkedin, label: "LinkedIn" },
];

const LEGAL = [
  { to: "/privacy", label: "Privacy Policy", id: "privacy" },
  { to: "/termini", label: "Termini & Condizioni", id: "termini" },
  { to: "/cookie", label: "Cookie Policy", id: "cookie" },
];

export const Footer = () => (
  <footer id="contatti" className="bg-[#0B0F17] pt-20 text-slate-400">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-14 md:grid-cols-12 lg:px-8">
      <div className="md:col-span-5">
        <Logo dark />
        <p className="mt-5 max-w-sm text-sm leading-relaxed">
          Piattaforma basata su Google Maps Platform e Google Solar API. Dati satellitari da oltre 40 paesi e 320 milioni di edifici.
        </p>
        <a data-testid="footer-whatsapp-button" onClick={() => track("click_whatsapp", { position: "footer" })} href={buildWhatsAppLink(GENERIC_WA_MESSAGE)} target="_blank" rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-[#1EBE5D]">
          <WhatsAppIcon /> Scrivici su WhatsApp
        </a>
      </div>
      <div className="md:col-span-3">
        <p className="text-sm font-semibold text-white">Contatti</p>
        <ul className="mt-5 space-y-3 text-sm">
          <li className="flex items-center gap-2"><Mail className="h-4 w-4" /> <a data-testid="footer-email" href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></li>
          <li className="flex items-center gap-2"><Phone className="h-4 w-4" /> <a data-testid="footer-phone" href={`tel:${COMPANY.phoneHref}`} className="hover:text-white">{COMPANY.phone}</a></li>
          <li className="flex items-center gap-2"><Globe className="h-4 w-4" /> <a data-testid="footer-web" href={COMPANY.webHref} target="_blank" rel="noopener noreferrer" className="hover:text-white">{COMPANY.web}</a></li>
          <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {COMPANY.address}</li>
        </ul>
      </div>
      <div className="md:col-span-2">
        <p className="text-sm font-semibold text-white">Sito</p>
        <ul className="mt-5 space-y-3 text-sm">
          {NAV.map((n) => (
            <li key={n.id}><button data-testid={`footer-link-${n.id}`} onClick={() => scrollToId(n.id)} className="hover:text-white">{n.label}</button></li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-2">
        <p className="text-sm font-semibold text-white">Legale</p>
        <ul className="mt-5 space-y-3 text-sm">
          {LEGAL.map((l) => (
            <li key={l.id}><Link to={l.to} data-testid={`footer-legal-${l.id}`} className="hover:text-white">{l.label}</Link></li>
          ))}
        </ul>
        <div className="mt-6 flex gap-3">
          {SOCIAL.map((s) => (
            <a key={s.label} href="#contatti" aria-label={s.label} data-testid={`footer-social-${s.label.toLowerCase()}`}
              className="grid h-10 w-10 place-items-center rounded-full border border-slate-700 transition-colors hover:border-emerald-500 hover:text-emerald-400">
              <s.icon className="h-4 w-4" />
            </a>
          ))}
        </div>
      </div>
    </div>
    <div className="border-t border-slate-800">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-6 text-xs sm:flex-row sm:items-center sm:justify-between lg:px-8">
        <div>
          <p data-testid="footer-copyright">© 2026 Pixa Solar – Apollo.AI. Tutti i diritti riservati.</p>
          <p data-testid="footer-piva" className="mt-1 text-slate-500">
            {COMPANY.legalName} · {COMPANY.address} · IDI/IVA {COMPANY.vat} · {COMPANY.email} | {COMPANY.web}
          </p>
        </div>
        <Link to="/admin" data-testid="footer-admin-link" className="hover:text-white">Area Staff</Link>
      </div>
    </div>
  </footer>
);
